import { useState, useEffect } from 'react';
import { profilesApi } from '../api/profiles';
import { getCachedBlobUrl, createBlobUrl } from '../api/files';

/**
 * Хук загрузки аватара как Blob URL.
 *
 * Аватары хранятся как UUID файлов на сервере.
 * `<img src="/api/files/{uuid}">` не работает — нет Bearer-токена.
 * Этот хук загружает аватар через `profilesApi.getAvatar()` (с auth)
 * и возвращает `blob:` URL для использования в `<img>`.
 *
 * Результаты кэшируются в общем `blobCache` из `api/files.js`.
 *
 * @param {string|null|undefined} avatarFileId — UUID файла аватара
 * @returns {string|null} — blob URL или null (если нет аватара / при ошибке)
 */
export function useAvatarUrl(avatarFileId) {
    const [url, setUrl] = useState(null);

    useEffect(() => {
        if (!avatarFileId) {
            setUrl(null);
            return;
        }

        // Если уже есть в кэше — используем сразу
        const cached = getCachedBlobUrl(avatarFileId);
        if (cached) {
            setUrl(cached.url);
            return;
        }

        let cancelled = false;

        const loadAvatar = async () => {
            try {
                const blob = await profilesApi.getAvatar(avatarFileId);
                if (cancelled) return;
                const blobUrl = createBlobUrl(avatarFileId, blob);
                if (!cancelled) setUrl(blobUrl);
            } catch (err) {
                if (!cancelled) {
                    console.warn('[useAvatarUrl] Failed to load avatar:', avatarFileId, err.message);
                }
            }
        };

        loadAvatar();

        return () => {
            cancelled = true;
        };
    }, [avatarFileId]);

    return url;
}
